import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  IconButton,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Inventory as ContainerIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useLocationCore } from '../../../../core/modules/location';
import { useContainerCore } from '../../../../core/modules/container';
import { useCompanyCore } from '../../../../core/modules/company';
import { Container } from '../../../../core/interfaces/container.interface';

interface LocateContainerDialogProps {
  open: boolean;
  containers: Container[];
  onClose: () => void;
  onSuccess: () => void;
  onRemoveContainer?: (containerId: number) => void;
}

export const LocateContainerDialog: React.FC<LocateContainerDialogProps> = ({
  open,
  containers,
  onClose,
  onSuccess,
  onRemoveContainer,
}) => {
  const { locations, getLocations } = useLocationCore();
  const { patchContainerLocation } = useContainerCore();
  const { companyId } = useCompanyCore();

  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar ubicaciones al abrir el diálogo
  useEffect(() => {
    if (open && companyId) {
      getLocations(companyId);
    }
  }, [open, companyId, getLocations]);

  // Limpiar estado al cerrar
  useEffect(() => {
    if (!open) {
      setSelectedLocation(null);
      setError(null);
    }
  }, [open]);

  const handleLocationChange = (_: any, value: any) => {
    setSelectedLocation(value);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!selectedLocation || !companyId) {
      setError('Debe seleccionar una ubicación');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ubicar todos los contenedores seleccionados
      const promises = containers.map(container =>
        patchContainerLocation(companyId, container.id, selectedLocation.id)
      );

      await Promise.all(promises);
      
      onSuccess();
      onClose();
    } catch (err) {
      setError('Error al ubicar los contenedores. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const selectedLocationData = selectedLocation;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LocationIcon color="primary" />
          <Typography variant="h6">
            Ubicar Contenedores
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Lista de contenedores seleccionados */}
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Contenedores a ubicar ({containers.length})
          </Typography>
          <List dense>
            {containers.map((container) => (
              <ListItem 
                key={container.id} 
                sx={{ px: 0 }}
                secondaryAction={
                  onRemoveContainer ? (
                    <IconButton
                      edge="end"
                      onClick={() => onRemoveContainer(container.id)}
                      size="small"
                      sx={{ color: 'error.main' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ backgroundColor: 'primary.main' }}>
                    <ContainerIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={container.code}
                  secondary={container.description || 'Sin descripción'}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Selector de ubicación */}
        <Autocomplete
          options={locations || []}
          getOptionLabel={(option) => `${option.id} - ${option.code} - ${option.description}`}
          value={selectedLocation}
          onChange={handleLocationChange}
          disabled={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Nueva Ubicación"
              placeholder="Buscar por ID, código o descripción..."
              variant="outlined"
            />
          )}
          noOptionsText="No hay ubicaciones disponibles"
          clearText="Limpiar"
          isOptionEqualToValue={(option, value) => option.id === value?.id}
        />

        {/* Información de la ubicación seleccionada */}
        {selectedLocationData && (
          <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
            <Typography variant="subtitle2" gutterBottom>
              Ubicación seleccionada:
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                label={selectedLocationData.code}
                color="primary"
                size="small"
                variant="outlined"
              />
              <Typography variant="body2">
                {selectedLocationData.description}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedLocation || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <LocationIcon />}
          sx={{ borderRadius: 2 }}
        >
          {loading ? 'Ubicando...' : 'Ubicar Contenedores'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
